import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SivaService } from 'src/api/v2';
import { SIVA_OPEN_REPORTING_YEARS_KEY, SIVA_PROGRAM_ADMIN_ESA_BILLS_KEY } from 'src/constants/hook-keys';

export function useOpenReportingYears(clusterId) {
  const queryClient = useQueryClient();
  const [service] = useState(() => new SivaService());

  const { data, ...rest } = useQuery({
    queryFn: () => service.getOpenReportingYears(),
    queryKey: [SIVA_OPEN_REPORTING_YEARS_KEY, clusterId],
  });

  const [validateMinDate, validateMaxDate] = useMemo(() => {
    const today = new Date();
    if (!data) return [today.getFullYear(), today.getFullYear()];
    const minYear = data.length > 0 ? Math.min(...data) : today.getFullYear();
    const maxYear = data.length > 0 ? Math.max(...data) : today.getFullYear();

    const minDate = new Date(minYear, 0, 1);
    const maxDate = new Date(maxYear, 11, 31);

    return [minDate, maxDate];
  }, [data]);

  const setOpenReportingYear = useMutation({
    mutationFn: (dto: { clusterId: string[]; year: number; open: boolean }) =>
      service.setOpenReportingYears(dto),
    onSuccess() {
      queryClient.invalidateQueries([[SIVA_PROGRAM_ADMIN_ESA_BILLS_KEY]]);
      queryClient.refetchQueries([[SIVA_PROGRAM_ADMIN_ESA_BILLS_KEY]]);
    },
  });

  return {
    openReportingYears: data || [],
    validateMinDate,
    validateMaxDate,
    setOpenReportingYear,
    ...rest,
  };
}
